import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { IGetNotificationsResponse } from '../models/GetNotificationsResponse';
import type { INotification } from '../models/Notification';
import type { INotificationSettingsResponse } from '../models/NotificationSettingsResponse';

const NOTIFICATIONS_API = '/umbraco/api/Notification';

/**
 * Request parameters for fetching notifications
 */
export interface GetNotificationsParams {
  showOnlyUnread: boolean;
  displayPeriod: number;
  listSize: number;
  currentLength: number;
}

/**
 * Request parameters for marking notification as read
 */
export interface MarkNotificationAsReadParams {
  notification: INotification;
  isRead: boolean;
}

/**
 * Request parameters for setting show only unread filter
 */
export interface SetShowOnlyUnreadFilterParams {
  isShowOnlyUnread: boolean;
}

/**
 * Generic API response wrapper
 */
interface ApiResponse<T> {
  Code: number;
  data?: T;
  message?: string;
}

/**
 * RTK Query API slice for notifications-related endpoints
 */
export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery,
  tagTypes: ['Notifications', 'NotificationSettings'],
  endpoints: (builder) => ({
    /**
     * Fetch notifications with filtering and pagination
     * Cached per request parameters
     */
    getNotifications: builder.query<IGetNotificationsResponse, GetNotificationsParams>({
      query: (params: GetNotificationsParams) => ({
        url: `${NOTIFICATIONS_API}/GetNotifications`,
        method: 'POST',
        body: { ...params, isSpa: true },
      }),
      transformResponse: (response: ApiResponse<IGetNotificationsResponse>) => {
        if (response.Code === 1 && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load notifications');
      },
      providesTags: (_result: any, _error: any, params: GetNotificationsParams) => [
        {
          type: 'Notifications' as const,
          id: `${params.showOnlyUnread}-${params.displayPeriod}-${params.listSize}-${params.currentLength}`,
        },
      ],
      keepUnusedDataFor: 60,
    }),

    /**
     * Fetch notification settings and configuration
     */
    getNotificationSettings: builder.query<INotificationSettingsResponse, void>({
      query: () => ({
        url: `${NOTIFICATIONS_API}/GetNotificationsSettings`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<INotificationSettingsResponse>) => {
        if (response.Code === 1 && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load notification settings');
      },
      providesTags: [{ type: 'NotificationSettings', id: 'SETTINGS' }],
      keepUnusedDataFor: 300,
    }),

    /**
     * Mark a single notification as read/unread
     */
    markNotificationAsRead: builder.mutation<void, MarkNotificationAsReadParams>({
      query: (params: MarkNotificationAsReadParams) => ({
        url: `${NOTIFICATIONS_API}/SetIsReadNotification`,
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (response.Code !== 1) {
          throw new Error(response.message || 'Failed to mark notification as read');
        }
        return undefined;
      },
      invalidatesTags: [{ type: 'Notifications' }, { type: 'NotificationSettings' }],
    }),

    /**
     * Mark all notifications as read
     */
    readAllNotifications: builder.mutation<void, void>({
      query: () => ({
        url: `${NOTIFICATIONS_API}/ReadAllNotifications`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (response.Code !== 1) {
          throw new Error(response.message || 'Failed to read all notifications');
        }
        return undefined;
      },
      invalidatesTags: [{ type: 'Notifications' }],
    }),

    /**
     * Set filter to show only unread notifications
     */
    setShowOnlyUnreadFilter: builder.mutation<void, SetShowOnlyUnreadFilterParams>({
      query: (params: SetShowOnlyUnreadFilterParams) => ({
        url: `${NOTIFICATIONS_API}/SetShowOnlyUnread`,
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (response.Code !== 1) {
          throw new Error(response.message || 'Failed to set filter');
        }
        return undefined;
      },
      invalidatesTags: [{ type: 'NotificationSettings', id: 'SETTINGS' }],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationSettingsQuery,
  useMarkNotificationAsReadMutation,
  useReadAllNotificationsMutation,
  useSetShowOnlyUnreadFilterMutation,
} = notificationsApi;

export const {
  endpoints: {
    getNotifications,
    getNotificationSettings,
    markNotificationAsRead,
    readAllNotifications,
    setShowOnlyUnreadFilter,
  },
  reducerPath,
  reducer,
  middleware,
} = notificationsApi;
